import classnames from 'classnames';
import css from 'styled-jsx/css';
import React, { useState, useEffect } from 'react';
import Pricer from '@yupwego/pricer';
import scrollTo from '../../lib/js/scrollTo';
import { colors, fonts } from '../common/Theme';

import Button from '../common/elements/Button.js';
import startOfDay from 'date-fns/startOfDay';
import subYears from 'date-fns/subYears';
import { strings, dates } from '../common/forms/Validations';

import ProfileForm from './ProfileForm';
import Quote from './Quote';
import uuid from 'uuid';

const hasSameKey = (lhs, rhs) => lhs.key === rhs.key;

const styles = css`
  .travelers {
    display: flex;
    flex-direction: column;
  }
  .beneficiaries {
    margin: 0;
    list-style: inside;
    padding: 0;
    font-family: ${colors.ywgSecondary};
    font-size: 1rem;
  }
  h2 {
    font-family: ${fonts.primaryFont};
  }
  .beneficiary-item {
    margin: 15px 0;
    list-style: none;
    background-color: white;
  }
  .beneficiaries__nav {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
  }

  .quote-total {
    font-size: 1rem;
    align-self: flex-end;
  }
`;
const buttonStyles = css.resolve`
  .button-nav {
    max-width: 50%;
    min-width: 100px;
    align-self: flex-end;
    width: 200px;
  }
  .last {
    margin-left: 12px;
  }
  .beneficiary-add {
    align-self: center;
    margin-bottom: 20px;
  }
`;

const newBeneficiary = () => {
  return {
    key: uuid.v4(),
    lastname: '',
    firstname: '',
    email: '',
    birthday: '',
    country: 'France'
  };
};

const beneficiaryValidators = () => {
  const today = startOfDay(new Date());
  const limit = startOfDay(subYears(new Date(), 64));
  return {
    key: [],
    firstname: [strings.required],
    lastname: [strings.required],
    birthday: [
      strings.required,
      dates
        .isBefore(today)
        .withError(
          `Notre contrat ne permet pas encore d'assurer les enfants avant leur naissance, mais nous serions ravis d'échanger sur votre besoin: contact@yupwego.com`
        ),
      dates
        .isAfter(limit)
        .withError(
          "L'offre YUPWEGO n'est pas encore disponible au delà de 64 ans, mais nous y travaillons. Contactez-nous et nous chercherons une solution ensemble."
        )
    ],
    country: [strings.required]
  };
};

const BeneficiariesStep = ({
  travel,
  beneficiaries,
  prices,
  className,
  onNext,
  onPrev
}) => {
  const initState = beneficiaries => {
    const beneficiary =
      beneficiaries && beneficiaries.length == 0 ? newBeneficiary() : null;
    return {
      beneficiaries: beneficiaries || [beneficiary],
      beneficiary: beneficiary
    };
  };
  const [state, setState] = useState(initState(beneficiaries));
  const addBeneficiary = () => {
    setState({ ...state, beneficiary: newBeneficiary() });
  };
  const saveBeneficiary = beneficiary => {
    const exists = state.beneficiaries.some(el => hasSameKey(beneficiary, el));
    const beneficiaries = exists
      ? state.beneficiaries.map(el =>
          hasSameKey(beneficiary, el) ? beneficiary : el
        )
      : [...state.beneficiaries, beneficiary];
    const beneficiariesByAge = beneficiaries.sort(
      (a, b) => a.birthday > b.birthday
    );
    setState({
      ...state,
      beneficiary: null,
      beneficiaries: beneficiariesByAge
    });
  };
  const clearBeneficiary = () => setState({ ...state, beneficiary: null });
  const editBeneficiary = beneficiary => {
    setState({ ...state, beneficiary: { ...beneficiary } });
  };
  const removeBeneficiary = beneficiary => {
    const updated = state.beneficiaries.filter(
      el => !hasSameKey(el, beneficiary)
    );
    setState({ ...state, beneficiary: null, beneficiaries: updated });
    if (updated.length === 0) {
      state.beneficiaries = updated;
      addBeneficiary();
    }
  };
  const title = state.beneficiaries.length < 2 ? 'Voyageur' : 'Voyageurs';
  const quotation = Pricer.quote(travel, state.beneficiaries, prices);
  const quotationBeneficiaries = quotation.details;
  useEffect(() => {
    scrollTo();
  });
  return (
    <div className={classnames(className, 'travelers')}>
      {quotationBeneficiaries.length > 0 && !state.beneficiary ? (
        <>
          <h2>{title}</h2>
          <ul className="beneficiaries">
            {quotationBeneficiaries.map(quotationBeneficiary => {
              return (
                <li
                  className="beneficiary-item"
                  key={quotationBeneficiary.beneficiary.key}
                >
                  <Quote
                    quotation={quotation}
                    removeBeneficiary={removeBeneficiary}
                    editBeneficiary={editBeneficiary}
                    quotationBeneficiary={quotationBeneficiary}
                    travel={travel}
                    prices={prices}
                  />
                </li>
              );
            })}
          </ul>
          <Button
            onClick={addBeneficiary}
            secondary
            className={classnames(buttonStyles.className, 'beneficiary-add')}
          >
            Ajouter un voyageur
          </Button>
          <h3 className="quote-total">
            Total estimé : {quotation.total.toFixed(2)} € / semaine
          </h3>
        </>
      ) : null}
      {state.beneficiary ? (
        <ProfileForm
          beneficiary={state.beneficiary}
          beneficiaryValidators={beneficiaryValidators}
          onAddBeneficiary={addBeneficiary}
          saveBeneficiary={saveBeneficiary}
          clearBeneficiary={clearBeneficiary}
          required={state.beneficiaries.length <= 0}
          className={className}
        />
      ) : null}
      {state.beneficiaries.length > 0 && !state.beneficiary ? (
        <>
          <div className="beneficiaries__nav">
            <Button
              className={classnames(buttonStyles.className, 'button-nav')}
              secondary
              arrow="left"
              onClick={() => onPrev(state.beneficiaries)}
            >
              Voyage
            </Button>
            <Button
              className={classnames(
                buttonStyles.className,
                'button-nav',
                'last'
              )}
              type="submit"
              arrow="right"
              onClick={() => onNext(state.beneficiaries)}
            >
              Souscripteur
            </Button>
          </div>
        </>
      ) : null}
      <style jsx>{styles}</style>
      {buttonStyles.styles}
    </div>
  );
};

export default BeneficiariesStep;
